<template>
    <section class="el-filter"
        :id="uid"
        :class="[
            collapsed ? 'el--collapsed' : '',
            uiShowActive ? 'el--viewactive' : '',
            uiCount > 0 ? 'el--hasselection' : '',
        ]">
        <header class="f fh c-p dw-10 sp-t-xs sp-b-xs" @click="toggleCollapsed()" v-if="uiShowHeader">
            <VisualSectionTitle :icon="icon"
                :iconSize="iconSize"
                :iconCname="iconCname"
                :name="uiTitle">
                <template #labelRight v-if="uiCount && uiCount > 0"><small class="el-badge sm-l-xs">{{ uiCount }}</small></template>
            </VisualSectionTitle>
            <span class="fg"></span>
            <CollapseTrigger v-model="collapsed" v-if="uiAdjustable" />
        </header>
        <div class="el-filter-main">
            <slot name="body"></slot>
        </div>
    </section>
</template>

<script>
import { guuid } from '@aretheyhappy/utils';
import VisualSectionTitle from './visuals/SectionTitle.vue';
import CollapseTrigger from './CollapseTrigger.vue';

export default {
    name: 'mead-filter-wrapper',
    props: {
        /**
         * Namespace/channel key used to scope this filter's Flash pub/sub events
         * (e.g. `${pubkey}:filter_open`), so multiple filter groups don't collide.
         */
        pubkey          : {type: String, default: () => ''},
        /**
         * Name of the icon shown in the header, passed through to `VisualSectionTitle`.
         */
        icon            : {type: [Boolean, String], default: () => false},
        /**
         * Additional class name applied to the header icon, passed through to
         * `VisualSectionTitle`.
         */
        iconCname       : {type: String, default: () => ''},
        /**
         * Size of the header icon, passed through to `VisualSectionTitle`.
         */
        iconSize        : {type: String, default: () => 'l'},
        /**
         * Title text shown in the header, passed through to `VisualSectionTitle`
         * as its `name`.
         */
        uiTitle         : {type: [Boolean, String], default: () => false},
        /**
         * Optional numeric badge shown next to the title when greater than 0; also
         * toggles the `el--hasselection` styling class on the wrapper.
         */
        uiCount         : {type: [Boolean, Number], default: () => false},
        /**
         * Whether the filter can be manually collapsed/expanded by the user via
         * the CollapseTrigger in the header.
         */
        uiAdjustable    : {type: Boolean, default: () => true},
        /**
         * Whether to render the clickable header (title, icon, collapse trigger)
         * at all.
         */
        uiShowHeader    : {type: Boolean, default: () => true},
        /**
         * Initial collapsed state of the filter body when the component mounts.
         */
        uiCollapsed     : {type: Boolean, default: () => false},
        /**
         * When true, applies the `el--viewactive` styling that keeps active
         * filter selections visible even while collapsed.
         */
        uiShowActive    : {type: Boolean, default: () => true},
    },
    components: {VisualSectionTitle, CollapseTrigger},
    data () {
        return {
            uid         : guuid(),
            collapsed   : this.uiCollapsed,
        };
    },
    methods: {
        onCollapseChanged: function () {
            if (this.collapsed) return;
            MeadCommon.Flash.publish(`${this.pubkey}:filter_open`, {uid: this.uid});
        },
        toggleCollapsed: function () {
            if (!this.uiAdjustable) return;
            this.collapsed = !this.collapsed;
            this.onCollapseChanged();
        },
    },
    watch: {
        collapsed: function () {
            this.onCollapseChanged();
        },
    },
    mounted () {
        //  Listen to filter open
        MeadCommon.Flash.subscribe(this.uid, `${this.pubkey}:filter_open`, ({uid}) => {
            if (uid !== this.uid) this.collapsed = true;
        });
    },
    destroyed () {
        MeadCommon.Flash.unsubscribe(this.uid);
    },
};
</script>

<style lang="scss">
@use '../../../sass/variables' as *;

.el-filter {
    padding: $s-xs 0 $s-s 0;

    //  MODIFIERS

    &:not(.el--viewactive).el--collapsed {
        padding: $s-xs 0;

        .el-filter-main {
            display: none;
        }
    }

    &.el--viewactive.el--collapsed {
        padding: $s-xs 0;

        .el-filter-main .el--inactive,
        .el-filter-main .el--default {
            display: none;
        }

        &:not(.el--hasselection) .el-filter-main {
            padding-top: 0;
        }

        &.el--hasselection .el-filter-main {
            padding-bottom: $s-xs;
        }
    }
}

.el-filter + .el-filter {
    border-top: 1px solid var(--c-panel-border);
}
</style>

<style lang="scss" scoped>
@use '../../../sass/variables' as *;

.el-filter-main {
    padding-top: $s-xs;
}
</style>
